import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { Entry } from 'src/types/contentful-sdk-compat';
import React from 'react';
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import { Box } from 'src/components-v2/Layout';
import { CmsMediaSwitch } from 'src/components-v2/Media';
import {
  ModeContextProvider,
  useCmsMode,
} from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { useLazyGuilloche } from 'src/hooks/useLazyGuilloche';
import useWorkerGuilloche from 'src/hooks/useWorkerGuilloche';
import { CmsComponents } from 'src/lib/constants';
import { resolveBackgroundColor } from 'src/lib/utils';
import styled from 'threads5/styles/styled';
import ThemeProvider from 'threads5/themes/theme-provider';

/**
 * This component overwrites the `mode` and `backgroundColor` fields of child components and remaps their
 * original values to `intrinsicMode` and `intrinsicBackgroundColor` properties. This type represents
 * that shaped for components nested within background containers to reference.
 */
export type BackgroundContainerAugmentedShape<
  T extends { mode?: string; backgroundColor?: string },
> = Omit<T, 'backgroundColor' | 'mode'> & {
  backgroundColor: string;
  mode: string;
  intrinsicMode: T['mode'];
  intrinsicBackgroundColor: T['backgroundColor'];
};

export type BackgroundContainerAugmentedEntry<T extends Entry<unknown>> = Omit<
  T,
  'fields'
> & {
  fields: BackgroundContainerAugmentedShape<T['fields']>;
};

/** Helper type for components which may or may not live within a background container. */
export type MaybeBackgroundContainerAugmentedEntry<T extends Entry<unknown>> =
  | T
  | BackgroundContainerAugmentedEntry<T>;

const sanitizeNumberInput = (input?: string | number): string | number => {
  if (typeof input === 'string') {
    return input.replace(/[^0-9.-]+/g, '');
  }
  return input;
};

export const Canvas = styled('canvas', {
  shouldForwardProp: (prop) => {
    return prop !== 'isGuillocheLoaded';
  },
})(({ isGuillocheLoaded }: { isGuillocheLoaded: boolean }) => {
  return {
    position: 'absolute',
    background: 'transparent',
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%)`,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.5s ease-in-out',
    opacity: isGuillocheLoaded ? 1 : 0,
  };
});

const BackgroundGuilloche = ({ fields, sys }) => {
  const canvas = React.useRef<HTMLCanvasElement>(null);
  const { guilloche, isGuillocheLoaded } = useLazyGuilloche({
    pattern: fields?.asset?.fields?.pattern,
    canvasRef: canvas,
    intersectionThreshold: fields?.asset?.fields?.intersectionThreshold,
  });

  const bgImg = fields?.asset?.fields?.backgroundAsset?.fields?.file?.url;
  const gradient = bgImg ? bgImg + '?fm=webp&q=1' : undefined;
  useWorkerGuilloche({
    canvasRef: canvas,
    disableAnimation: fields?.asset?.fields?.disableAnimation,
    gradient,
    horizontalWave: fields?.asset?.fields?.xAxis,
    id: sys?.id,
    intensity: fields?.asset?.fields?.intensity,
    intersectionThreshold: fields?.asset?.fields?.intersectionThreshold,
    rotate: sanitizeNumberInput(fields?.asset?.fields?.translate?.rotate),
    scale: sanitizeNumberInput(
      fields?.asset?.fields?.translate?.scale ??
        fields?.asset?.fields?.translate?.z,
    ),
    scaleX: sanitizeNumberInput(fields?.asset?.fields?.translate?.scaleX),
    scaleY: sanitizeNumberInput(fields?.asset?.fields?.translate?.scaleY),
    speed: fields?.asset?.fields?.speed,
    svgHeight: guilloche?.height,
    svgPaths: guilloche?.paths,
    svgWidth: guilloche?.width,
    verticalWave: fields?.asset?.fields?.yAxis,
    x: sanitizeNumberInput(fields?.asset?.fields?.translate?.x),
    y: sanitizeNumberInput(fields?.asset?.fields?.translate?.y),
    zAxisWave: fields?.asset?.fields?.zAxis,
  });
  return (
    <Canvas
      id={sys?.id}
      className='guilloche-canvas'
      ref={canvas}
      isGuillocheLoaded={isGuillocheLoaded}
      // NOTE: we are no longer allowing the styles field to be used
    />
  );
};

const BackgroundAsset = ({ fields, sys }) => {
  const asset = fields?.asset?.fields;
  const baseParams = '?fm=webp&q=70';

  return (
    <Box
      className='cms-background-image'
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        '.lazyload-wrapper': {
          width: '100%',
          height: '100%',
        },
      }}
    >
      <CmsMediaSwitch
        asset={asset}
        queryString={{
          xs: baseParams + '&w=639',
          sm: baseParams + '&w=1023',
          md: baseParams + '&w=1279',
          lg: baseParams + '&w=1439',
          xl: baseParams + '&w=1920',
        }}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );
};

const BackgroundSwitcher = ({ fields, sys }) => {
  switch (fields?.asset?.sys?.contentType?.sys?.id) {
    case CmsComponents.GUILLOCHE:
      return <BackgroundGuilloche fields={fields} sys={sys} />;
    case CmsComponents.ASSET:
      return <BackgroundAsset fields={fields} sys={sys} />;
  }
  return null;
};

export const BackgroundContainer = ({ fields, sys, ...props }) => {
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);

  const background = resolveBackgroundColor(
    componentTheme,
    fields?.backgroundColor,
  );

  // AEO wrappers group components without owning their visual background.
  // Apply the container overrides through those wrappers, while leaving
  // other components responsible for styling their own descendants.
  const inheritBackground = (component) => {
    return {
      ...component,
      fields: {
        ...component.fields,
        ...(component?.sys?.contentType?.sys?.id ===
          CmsComponents.HIDE_FROM_AEO && {
          components: component.fields?.components?.map(inheritBackground),
        }),
        backgroundColor: 'transparent',
        mode,
        intrinsicMode: component?.fields?.mode,
        intrinsicBackgroundColor: component?.fields?.backgroundColor,
      },
    };
  };
  const components = fields?.components?.map(inheritBackground);

  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  return (
    <ModeContextProvider mode={mode}>
      <ThemeProvider theme={componentTheme}>
        <Box
          className='background-container'
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background,
            ...fields?.styles,
          }}
          {...inspectorProps({ fieldId: 'components' })}
        >
          {fields?.asset?.fields && (
            <BackgroundSwitcher fields={fields} sys={sys} />
          )}
          <Box
            sx={{ position: 'relative' }}
            className='background-container-inner'
          >
            <CmsComponentSwitch {...props} components={components} />
          </Box>
        </Box>
      </ThemeProvider>
    </ModeContextProvider>
  );
};
